/**
 * Powered by WunderGuide attribution row at the bottom of the iframe.
 * Hidden when feature_flags.white_label is true.
 *
 * The logotype SVG is rendered via CSS mask-image + currentColor so it
 * adopts the surrounding text color — works on both light and dark themes
 * without per-mode assets.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useInstance } from '../context/InstanceContext';
import { useAuth } from '../hooks/useAuth';
import { useView } from '../context/ViewContext';
import envConfig from '../../config/env.json';

const REDEEM_BASE_URL: string =
  (envConfig as { REDEEM_BASE_URL?: string }).REDEEM_BASE_URL ||
  'https://go.wunderguide.com/redeem';

const LOGOTYPE_BY_LANG: Record<string, { src: string; aspect: number }> = {
  // viewBox aspect ratios from the source SVGs
  default: {
    src: '/logos/WunderGuide-logotype-white.svg',
    aspect: 944.4 / 125.9,
  },
  ja: { src: '/logos/WunderGuide-logotype-white-ja.svg', aspect: 951.9 / 158 },
  zh: { src: '/logos/WunderGuide-logotype-white-zh.svg', aspect: 624.7 / 158 },
  ko: { src: '/logos/WunderGuide-logotype-white-ko.svg', aspect: 664.2 / 158 },
};

function pickLogotype(locale: string): { src: string; aspect: number } {
  const lang = (locale || 'en').toLowerCase().split('-')[0];
  return LOGOTYPE_BY_LANG[lang] || LOGOTYPE_BY_LANG.default;
}

const FOOTER_HEIGHT = 14; // px — the SVG mask height

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { config, effectiveLocale } = useInstance();
  const { authState } = useAuth();
  const { setView } = useView();
  const whiteLabel = !!config?.feature_flags?.white_label;
  if (whiteLabel) return null;

  // Voluntary sign-up: shown on registration-capable instances
  // (feature_flags.registration_gate) to a guest — registration is NOT
  // enforced (see useGatedAddItem), this just lets them save across devices
  // if they choose. Registered users (authState 'authenticated') don't see it.
  const showSignup =
    config?.feature_flags?.registration_gate === true &&
    authState !== 'authenticated';

  const logo = pickLogotype(effectiveLocale);
  const width = Math.round(FOOTER_HEIGHT * logo.aspect);

  // Route the footer through the instance's paired campaign so installs and
  // bookings get attributed back to the partner. Falls back to the plain
  // marketing URL on legacy instances missing a campaign code.
  const campaignCode = config?.campaign_code;
  const href = campaignCode
    ? `${REDEEM_BASE_URL}?code=${encodeURIComponent(campaignCode)}`
    : 'https://wunderguide.com/';

  // Some languages need a closing particle around the brand to read
  // naturally — e.g. Chinese `由 X 提供` ("by X provided") or Hawaiian
  // `Mai X mai` ("from X from"). Most langs don't define the key at all,
  // and our i18n init has `returnEmptyString: false`, which means a
  // bare `t()` would surface the literal key string for missing langs.
  // Gate on i18n.exists() so the suffix span only renders for langs
  // that explicitly declare the key — anything else gets nothing.
  const suffix = i18n.exists('footer.powered_by_suffix')
    ? t('footer.powered_by_suffix')
    : '';

  return (
    <div className="wg-footer-area">
      {showSignup && (
        <button
          type="button"
          className="wg-footer__signup"
          onClick={() => setView('auth')}
        >
          {t('footer.signup_prompt', 'Sign in to save your trip')}
        </button>
      )}
      <div className="wg-footer" aria-label={t('footer.powered_by_aria')}>
        <span className="wg-footer__prefix">
          {t('footer.powered_by_prefix')}
        </span>
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="wg-footer__logo-link"
          aria-label={t('footer.wunderguide')}
        >
          <span
            className="wg-footer__logo"
            style={{
              width,
              height: FOOTER_HEIGHT,
              // CSS mask handles all browsers; we set both prefixed and
              // unprefixed so Safari + Firefox both work.
              WebkitMaskImage: `url(${logo.src})`,
              maskImage: `url(${logo.src})`,
            }}
          />
        </a>
        {suffix && <span className="wg-footer__suffix">{suffix}</span>}
      </div>
    </div>
  );
};

export default Footer;
